import Button, { ButtonProps } from '@pelckmans/elementis/components/button';
import ButtonDropdown from '@pelckmans/elementis/components/button-dropdown';
import PillGroup from '@pelckmans/elementis/components/pill-group';
import MaterialWordListIcon from '@pelckmans/elementis/icons/material-wordlist';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../../constants/analyticsEvents';
import ModalContext from '../../../../../contexts/ModalContext';
import useAnalytics from '../../../../../hooks/useAnalytics';
import useModuleLicenseCheck from '../../../../../hooks/useModuleLicenseCheck';
import AppsContext from '../../../_contexts/AppsContext';
import ModuleContext from '../../../_contexts/ModuleContext';
import { App, LinkAppTypes, PlatformTypes } from '../../../api/appsApi';
import ModuleHeaderTag from '../../../components/ModuleHeaderTag';
import WordlistModal from '../modals/WordlistModal';

const getDisabledTooltip = (isUserLicensed: boolean, app: App): string | undefined => {
  if (app.state === 'preview') return 'PAGES.TOC_LEVEL.APPS.WORD_LIST.PREVIEW_TOOLTIP';
  if (!isUserLicensed) return 'PAGES.TOC_LEVEL.APPS.WORD_LIST.NO_LICENSE_TOOLTIP';
};

export default function WordListAppButton({ size = 'md' }: { size?: ButtonProps['size'] }) {
  const { t } = useTranslation();
  const { isUserLicensed } = useModuleLicenseCheck();
  const { capture } = useAnalytics();

  const { apps } = useContext(AppsContext);
  const { module } = useContext(ModuleContext);
  const { showModal } = useContext(ModalContext);

  const handleOpenWordlist = async (app: App) => {
    if (!module) return;

    capture({
      objectId: app.id,
      eventType: ANALYTICS_EVENT_TYPES.APP_ACCESSED,
      subLocationId: module.isExerciseModule
        ? ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_EXERCISE_MODULE_MATERIAL
        : ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MATERIAL,
    });

    return showModal(
      <WordlistModal
        id="wordlist-modal"
        wordlistId={app?.linkedApp?.id!}
        name={app.name}
        module={module!}
        headerProps={{
          title: app.name,
          titleTooltip: app.name,
          tags: (
            <PillGroup aria-label={undefined} role={undefined}>
              <ModuleHeaderTag module={module} />
            </PillGroup>
          ),
        }}
      />,
    );
  };

  if (!module) return null;

  const wordLists = apps.filter(
    app =>
      app.platformType === PlatformTypes.P_INTERN &&
      app.platformLinkType === LinkAppTypes.WORDLIST &&
      app.moduleId === module?.id,
  );

  if (wordLists.length === 0) return null;

  if (wordLists.length === 1) {
    const wordList = wordLists[0];
    const disabledTooltip = getDisabledTooltip(isUserLicensed!, wordList);
    return (
      <Button
        key="word-list-button"
        startIcon={<MaterialWordListIcon data-testid="word-list-icon" />}
        prominence="secondary"
        size={size}
        onClick={() => handleOpenWordlist(wordLists[0])}
        disabled={!!disabledTooltip}
        tooltip={disabledTooltip ? t(disabledTooltip) : undefined}
      >
        {t('PAGES.TOC_LEVEL.APPS.WORD_LIST.OPEN_WORD_LIST')}
      </Button>
    );
  }

  return (
    <ButtonDropdown
      title={t('PAGES.TOC_LEVEL.APPS.WORD_LIST.OPEN_WORD_LIST')}
      size={size}
      key="word-list-button"
      buttonProps={{
        prominence: 'secondary',
        startIcon: <MaterialWordListIcon data-testid="word-list-icon" />,
      }}
      disableBackgroundScroll
    >
      {wordLists.map(wordList => {
        const disabledTooltip = getDisabledTooltip(isUserLicensed!, wordList);
        const disabledProps = disabledTooltip ? ({ disabled: true, tooltip: t(disabledTooltip) } as const) : {};

        return (
          <ButtonDropdown.Item
            as="button"
            key={wordList.id}
            value={wordList.id}
            text={wordList.name}
            textIcon={<MaterialWordListIcon data-testid={`word-list-icon-${wordList.id}`} />}
            onClick={() => handleOpenWordlist(wordList)}
            {...disabledProps}
          />
        );
      })}
    </ButtonDropdown>
  );
}
